import { FC } from 'react';
import dynamic from 'next/dynamic';

import { cn } from '@virginexperiencedays/tailwind';
import { useWindowWidth } from '@virginexperiencedays/hooks';

import { HeroCarousel } from './HeroCarousel';
import { PromotionDiscount } from './PromotionDiscount';

import type { HeroStaticImageProps } from './HeroImageDesktop';
import type { Image } from '@virginexperiencedays/products';
import type { ProductPromo } from '@virginexperiencedays/promos';

const HeroImageDesktop = dynamic(
  () => import('./HeroImageDesktop').then((mod) => mod.HeroStaticImage),
  {
    ssr: false,
    loading: () => <HeroCarousel productName={null} images={[fallbackImage as unknown as Image]} />,
  }
) as FC<HeroStaticImageProps>;

export type HeroImagesProps = {
  /**
   * Used to display as fallback alt text if no alt text is provided on the image
   */
  productName: string;
  className?: string;
  /**
   * The images to display in the carousel.
   */
  images: Image[];
  /**
   * CTA to trigger modal on desktop view
   */
  onClick: () => void;
  /* Product price for Black Friday discount */
  price: any;
  /**
   * Denotes Promo product
   */
  productPromo: ProductPromo | null;
};

const fallbackImage = {
  url: 'https://images.prismic.io/virginexperiencedays/6288bdd2-0da0-4db1-bc60-1403f1840a19_gray.jpeg?auto=compress,format',
};

export const HeroImages = ({
  className = null,
  images,
  onClick,
  productName,
  price,
  productPromo,
  ...props
}: HeroImagesProps) => {
  const { width } = useWindowWidth();

  return (
    <div className={cn('relative w-full md:sticky md:top-6', className)} data-testid="hero-images">
      {width > 768 ? (
        <HeroImageDesktop
          className="hidden md:block"
          alt={productName}
          image={images.length < 1 ? (fallbackImage as unknown as Image) : images[0]}
          onClick={onClick}
        />
      ) : (
        <HeroCarousel
          className="md:hidden"
          images={images.length < 1 ? [fallbackImage as unknown as Image] : images}
          productName={productName}
        />
      )}
    </div>
  );
};

type PromotionComponentProps = {
  // current badge props
  readonly price?: Record<string, number>;
  readonly promos?: Record<string, boolean>;
};

function PromotionComponent({ price, promos }: PromotionComponentProps) {
  return <PromotionDiscount price={price} {...promos} />;
}
